import React, { useState, useEffect } from 'react';
import {
  Box,
  Flex,
  Text,
  Button,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Badge,
  IconButton,
  useColorModeValue,
  useToast,
  useDisclosure,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
} from '@chakra-ui/react';
import { AddIcon, ViewIcon, ChevronDownIcon } from '@chakra-ui/icons';
import Card from 'components/card/Card';
import { getAllPayrollPeriods } from 'services/payroll.service';
import CreatePayrollPeriodModal from 'components/payroll/CreatePayrollPeriodModal';
import PayrollListView from 'components/payroll/PayrollListView';

export default function PayrollPeriods() {
  const [periods, setPeriods] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedPeriod, setSelectedPeriod] = useState(null);
  const [viewMode, setViewMode] = useState('list'); // 'list' | 'payrolls'

  const {
    isOpen: isCreateOpen,
    onOpen: onCreateOpen,
    onClose: onCreateClose,
  } = useDisclosure();

  const toast = useToast();
  const textColor = useColorModeValue('secondaryGray.900', 'white');
  const borderColor = useColorModeValue('gray.200', 'whiteAlpha.100');
  const bgCard = useColorModeValue('white', 'navy.800');

  useEffect(() => {
    loadPeriods();
  }, []);

  const loadPeriods = async () => {
    try {
      setLoading(true);
      const response = await getAllPayrollPeriods();
      if (response.status === 200) {
        setPeriods(response.payload.periods || []);
      }
    } catch (error) {
      toast({
        title: 'Error',
        description: 'No se pudieron cargar los períodos de liquidación',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleViewPayrolls = (period) => {
    setSelectedPeriod(period);
    setViewMode('payrolls');
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'DRAFT':
        return 'gray';
      case 'OPEN':
        return 'blue';
      case 'CALCULATED':
        return 'yellow';
      case 'APPROVED':
        return 'green';
      case 'PAID':
        return 'purple';
      case 'CLOSED':
        return 'gray';
      default:
        return 'gray';
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case 'DRAFT':
        return 'Borrador';
      case 'OPEN':
        return 'Abierto';
      case 'CALCULATED':
        return 'Calculado';
      case 'APPROVED':
        return 'Aprobado';
      case 'PAID':
        return 'Pagado';
      case 'CLOSED':
        return 'Cerrado';
      default:
        return status;
    }
  };

  const getMonthName = (month) => {
    const months = [
      'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
      'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'
    ];
    return months[month - 1];
  };

  if (viewMode === 'payrolls' && selectedPeriod) {
    return (
      <PayrollListView
        period={selectedPeriod}
        onBack={() => {
          setViewMode('list');
          setSelectedPeriod(null);
          loadPeriods();
        }}
      />
    );
  }

  if (loading) return null;

  return (
    <Box pt={{ base: '130px', md: '80px', xl: '80px' }}>
      <Card bg={bgCard} p="20px">
        {/* Header */}
        <Flex justify="flex-end" align="center" mb="20px">
          <Button
            leftIcon={<AddIcon />}
            colorScheme="blue"
            onClick={onCreateOpen}
          >
            Nuevo Período
          </Button>
        </Flex>

        {/* Table */}
        <Box overflowX="auto">
          <Table variant="simple" color={textColor}>
            <Thead>
              <Tr>
                <Th borderColor={borderColor}>Período</Th>
                <Th borderColor={borderColor}>Descripción</Th>
                <Th borderColor={borderColor}>Estado</Th>
                <Th borderColor={borderColor}>Liquidaciones</Th>
                <Th borderColor={borderColor}>Total a Pagar</Th>
                <Th borderColor={borderColor}>Acciones</Th>
              </Tr>
            </Thead>
            <Tbody>
              {periods.length === 0 ? (
                <Tr>
                  <Td colSpan={6} textAlign="center" py="40px">
                    <Text color="gray.500">
                      No hay períodos de liquidación registrados
                    </Text>
                  </Td>
                </Tr>
              ) : (
                periods.map((period) => (
                  <Tr key={period.id}>
                    <Td borderColor={borderColor}>
                      <Text fontWeight="500">
                        {getMonthName(period.month)} {period.year}
                      </Text>
                    </Td>
                    <Td borderColor={borderColor}>
                      <Text fontSize="sm">{period.description || '-'}</Text>
                    </Td>
                    <Td borderColor={borderColor}>
                      <Badge colorScheme={getStatusColor(period.status)}>
                        {getStatusLabel(period.status)}
                      </Badge>
                    </Td>
                    <Td borderColor={borderColor}>
                      <Text fontSize="sm">{period.payroll_count || 0}</Text>
                    </Td>
                    <Td borderColor={borderColor}>
                      <Text fontSize="sm" fontWeight="500">
                        ${' '}
                        {(period.total_net_salary || 0).toLocaleString('es-UY', {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2,
                        })}
                      </Text>
                    </Td>
                    <Td borderColor={borderColor}>
                      <Flex gap={2}>
                        <IconButton
                          icon={<ViewIcon />}
                          size="sm"
                          colorScheme="blue"
                          variant="ghost"
                          onClick={() => handleViewPayrolls(period)}
                          aria-label="Ver liquidaciones"
                        />
                      </Flex>
                    </Td>
                  </Tr>
                ))
              )}
            </Tbody>
          </Table>
        </Box>
      </Card>

      {/* Modal de crear período */}
      {isCreateOpen && (
        <CreatePayrollPeriodModal
          isOpen={isCreateOpen}
          onClose={onCreateClose}
          onSuccess={() => {
            loadPeriods();
            onCreateClose();
          }}
        />
      )}
    </Box>
  );
}
